/*
 * TECH-DEBT(VIS-327): dates in the tenant's time zone, until the VIS-327
 * tenant formatter replaces this. The zone is the IANA `timeZone` the BFF
 * sends with each response; a blank or unknown zone falls back to
 * Europe/London. The format is en-GB, as in the invitation email:
 * "12 October 2026, 16:20 BST" (24-hour time, zone abbreviation).
 */

export const FALLBACK_TIME_ZONE = 'Europe/London'

function zoneOrFallback(timeZone: string | null | undefined): string {
  const zone = timeZone?.trim()
  if (!zone) return FALLBACK_TIME_ZONE
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone })
    return zone
  } catch {
    return FALLBACK_TIME_ZONE
  }
}

function parts(iso: string, timeZone: string | null | undefined, withTime: boolean) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const format = new Intl.DateTimeFormat('en-GB', {
    timeZone: zoneOrFallback(timeZone),
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short' } : {}),
  } as Intl.DateTimeFormatOptions)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    format.formatToParts(date).find((part) => part.type === type)?.value ?? ''
  return { day: value('day'), month: value('month'), year: value('year'), hour: value('hour'), minute: value('minute'), zone: value('timeZoneName') }
}

/** "12 October 2026, 16:20 BST"; an unparseable instant is returned as given. */
export function formatTenantDateTime(iso: string, timeZone: string | null | undefined): string {
  const p = parts(iso, timeZone, true)
  if (!p) return iso
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} ${p.zone}`
}

/** "12 October 2026"; an unparseable instant is returned as given. */
export function formatTenantDate(iso: string, timeZone: string | null | undefined): string {
  const p = parts(iso, timeZone, false)
  if (!p) return iso
  return `${p.day} ${p.month} ${p.year}`
}

/*
 * Tenant wall times (VIS-460). The BFF sends some times as tenant-local
 * date-times with no offset ("2026-10-14T19:30:00"), and some dates as
 * calendar dates ("2026-10-14"). Neither is ever shifted into the browser's
 * zone: they are read as written and formatted in UTC. An instant is first
 * turned into the tenant's wall time. All of them are shown without a zone
 * abbreviation, because every time on the page is the tenant's.
 */

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?$/

type Wall = { year: number; month: number; day: number; hour: number; minute: number; hasTime: boolean }

function readLocal(value: string): Wall | null {
  const match = LOCAL.exec(value.trim())
  if (!match) return null
  const [, y, m, d, hh, mm] = match
  return { year: +y, month: +m, day: +d, hour: hh ? +hh : 0, minute: mm ? +mm : 0, hasTime: hh !== undefined }
}

const pad = (value: number) => String(value).padStart(2, '0')

const wallFormat = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...options })
const DATE_FORMAT = wallFormat({ day: 'numeric', month: 'long', year: 'numeric' })
const MONTH_FORMAT = wallFormat({ month: 'long', year: 'numeric' })

const asUtc = (wall: Wall) => new Date(Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute))
const timeOf = (wall: Wall) => `${pad(wall.hour)}:${pad(wall.minute)}`

/** The tenant's wall time of an instant, as "YYYY-MM-DDTHH:mm"; null when unparseable. */
export function toTenantLocal(iso: string, timeZone: string | null | undefined): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const format = new Intl.DateTimeFormat('en-GB', {
    timeZone: zoneOrFallback(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const value = (type: Intl.DateTimeFormatPartTypes) => format.formatToParts(date).find((part) => part.type === type)?.value ?? ''
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`
}

/** "14 October 2026, 19:30" for a tenant-local date-time; "14 October 2026" when it has no time. */
export function formatLocalDateTime(local: string): string {
  const wall = readLocal(local)
  if (!wall) return local
  const date = DATE_FORMAT.format(asUtc(wall))
  return wall.hasTime ? `${date}, ${timeOf(wall)}` : date
}

/** "19:30" for a tenant-local date-time. */
export function formatLocalTime(local: string): string {
  const wall = readLocal(local)
  return wall?.hasTime ? timeOf(wall) : local
}

/** "14 October 2026" for a calendar date such as a membership's end. */
export function formatCalendarDate(date: string): string {
  const wall = readLocal(date)
  return wall ? DATE_FORMAT.format(asUtc(wall)) : date
}

/** "October 2026", the month heading of a tenant-local date-time. */
export function formatMonthHeading(local: string): string {
  const wall = readLocal(local)
  return wall ? MONTH_FORMAT.format(asUtc({ ...wall, day: 1 })) : local
}

/** "14 October 2026, 09:00" for an instant, in the tenant's wall time. */
export function formatTenantInstant(iso: string, timeZone: string | null | undefined): string {
  const local = toTenantLocal(iso, timeZone)
  return local ? formatLocalDateTime(local) : iso
}

/** Where a tenant-local time falls against now in the tenant's zone. */
export type TenantDay = 'later-today' | 'earlier-today' | 'other'

export function tenantDayOf(local: string, timeZone: string | null | undefined, now: Date = new Date()): TenantDay {
  const wall = readLocal(local)
  const nowLocal = toTenantLocal(now.toISOString(), timeZone)
  if (!wall || !nowLocal) return 'other'
  const day = `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`
  if (day !== nowLocal.slice(0, 10)) return 'other'
  return timeOf(wall) > nowLocal.slice(11, 16) ? 'later-today' : 'earlier-today'
}

/** The "YYYY-MM" of a tenant-local date-time, for grouping by month. */
export function monthKeyOf(local: string): string | null {
  const wall = readLocal(local)
  return wall ? `${wall.year}-${pad(wall.month)}` : null
}

/*
 * Typed calendar dates (VIS-330), for the date field. A date is shown and
 * typed in the tenant's pattern and travels as an ISO calendar date
 * ("2026-10-13"). Until VIS-327 every tenant's pattern is en-GB
 * "dd/mm/yyyy"; the day and month may be typed with one digit, and "/",
 * ".", "-" or a space may separate the parts.
 */
export function tenantDatePattern(): string {
  return 'dd/mm/yyyy'
}

const PATTERN_PARTS = tenantDatePattern().split('/') as ('dd' | 'mm' | 'yyyy')[]

function realDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/** "13/10/2026" for "2026-10-13"; anything else is returned as given. */
export function formatDateInput(isoDate: string): string {
  const wall = readLocal(isoDate)
  if (!wall || wall.hasTime) return isoDate
  const value = { dd: pad(wall.day), mm: pad(wall.month), yyyy: String(wall.year) }
  return PATTERN_PARTS.map((part) => value[part]).join('/')
}

/** The ISO calendar date typed in the tenant's pattern, or null when it isn't a real date. */
export function parseDateInput(text: string): string | null {
  const parts = text.trim().split(/\s*[/.\- ]\s*/)
  if (parts.length !== PATTERN_PARTS.length) return null
  const value: Record<string, number> = {}
  for (const [index, part] of PATTERN_PARTS.entries()) {
    const digits = parts[index]
    if (!(part === 'yyyy' ? /^\d{4}$/ : /^\d{1,2}$/).test(digits)) return null
    value[part] = Number(digits)
  }
  if (!realDate(value.yyyy, value.mm, value.dd)) return null
  return `${value.yyyy}-${pad(value.mm)}-${pad(value.dd)}`
}

/** Today in the tenant's time zone, as "YYYY-MM-DD". */
export function tenantToday(timeZone: string | null | undefined, now: Date = new Date()): string {
  return (toTenantLocal(now.toISOString(), timeZone) ?? now.toISOString()).slice(0, 10)
}

/** The same calendar day `years` later (or earlier); 29 February becomes 28 February in a common year. */
export function shiftYears(isoDate: string, years: number): string {
  const wall = readLocal(isoDate)
  if (!wall) return isoDate
  const year = wall.year + years
  const day = realDate(year, wall.month, wall.day) ? wall.day : wall.day - 1
  return `${year}-${pad(wall.month)}-${pad(day)}`
}

/*
 * Tenant calendar days (VIS-490). A day is an ISO calendar date
 * ("2026-10-16") with no zone: stepping it is calendar arithmetic, so a BST
 * change never moves it, and the BFF turns it into the tenant's day window.
 */

/** Whether `value` is a real calendar date written as "YYYY-MM-DD". */
export function isIsoDate(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  return realDate(year, month, day)
}

/** The calendar day `days` after (or before) `isoDate`; anything else is returned as given. */
export function shiftDays(isoDate: string, days: number): string {
  if (!isIsoDate(isoDate)) return isoDate
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day + days))
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** The same day `months` later (or earlier), kept within the shorter month: 31 January plus one month is 28 or 29 February. */
export function shiftMonths(isoDate: string, months: number): string {
  if (!isIsoDate(isoDate)) return isoDate
  const [year, month, day] = isoDate.split('-').map(Number)
  const last = new Date(Date.UTC(year, month - 1 + months + 1, 0)).getUTCDate()
  const date = new Date(Date.UTC(year, month - 1 + months, Math.min(day, last)))
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** The number of calendar days from `from` to `to`, negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const utc = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-').map(Number)
    return Date.UTC(year, month - 1, day)
  }
  return Math.round((utc(to) - utc(from)) / 86_400_000)
}

/** 0 for Monday to 6 for Sunday, the en-GB week. */
export function weekdayIndex(isoDate: string): number {
  const [year, month, day] = isoDate.split('-').map(Number)
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7
}

const LONG_DATE_FORMAT = wallFormat({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

/** "Friday 16 October 2026" for a calendar date; anything else is returned as given. */
export function formatLongDate(isoDate: string): string {
  if (!isIsoDate(isoDate)) return isoDate
  const [year, month, day] = isoDate.split('-').map(Number)
  const parts = LONG_DATE_FORMAT.formatToParts(new Date(Date.UTC(year, month - 1, day)))
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${value('weekday')} ${value('day')} ${value('month')} ${value('year')}`
}

/** The en-GB weekdays from Monday, short and full, for a calendar's column headers. */
export function weekdayNames(): { short: string; full: string }[] {
  const short = wallFormat({ weekday: 'short' })
  const full = wallFormat({ weekday: 'long' })
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(Date.UTC(2024, 0, 1 + index))
    return { short: short.format(date).slice(0, 2), full: full.format(date) }
  })
}

/** The time zone's name, such as "United Kingdom Time" for Europe/London. */
export function formatTenantZoneName(timeZone: string | null | undefined): string {
  const zone = zoneOrFallback(timeZone)
  const name = new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'longGeneric' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value
  return name ?? zone
}
